import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  MergeType as MergeIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { mergeRequestsApi } from '../../api';
import type { OfflinePlayer, BoardPresenceInfo, GlobalMergeRequestResult } from '../../types';

export interface GlobalMergeConfirmDialogProps {
  open: boolean;
  offlinePlayer: OfflinePlayer | null;
  targetUserId: string;
  targetUserName: string;
  onClose: () => void;
  onMergeCreated: (result: GlobalMergeRequestResult) => void;
}

/**
 * A confirmation dialog for creating global merge requests.
 * Shows which boards will be affected and requires user confirmation.
 */
export const GlobalMergeConfirmDialog: React.FC<GlobalMergeConfirmDialogProps> = ({
  open,
  offlinePlayer,
  targetUserId,
  targetUserName,
  onClose,
  onMergeCreated,
}) => {
  const [boardPresence, setBoardPresence] = useState<BoardPresenceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch board presence when dialog opens
  const fetchBoardPresence = useCallback(async () => {
    if (!offlinePlayer || !targetUserId) return;

    setLoading(true);
    setError(null);
    try {
      const presence = await mergeRequestsApi.getOfflinePlayerBoards(
        offlinePlayer.id,
        targetUserId
      );
      setBoardPresence(presence);
    } catch (_err) {
      setError('Failed to fetch board information');
      setBoardPresence([]);
    } finally {
      setLoading(false);
    }
  }, [offlinePlayer, targetUserId]);

  useEffect(() => {
    if (open && offlinePlayer && targetUserId) {
      fetchBoardPresence();
    } else {
      setBoardPresence([]);
    }
  }, [open, offlinePlayer, targetUserId, fetchBoardPresence]);

  // All boards are included (target user will be auto-added where not a member)
  const totalSquares = useMemo(
    () => boardPresence.reduce((sum, b) => sum + b.squareCount, 0),
    [boardPresence]
  );

  const totalLedgerEntries = useMemo(
    () => boardPresence.reduce((sum, b) => sum + b.ledgerEntries, 0),
    [boardPresence]
  );

  // Count boards where target user will be auto-added
  const boardsNeedingAutoAdd = useMemo(
    () => boardPresence.filter(b => !b.targetUserIsMember).length,
    [boardPresence]
  );

  const handleClose = () => {
    setError(null);
    setBoardPresence([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!offlinePlayer) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await mergeRequestsApi.createGlobal(offlinePlayer.id, {
        targetUserId,
        removeOfflinePlayer: true,
      });
      onMergeCreated(result);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create merge request');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !loading && !submitting && boardPresence.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MergeIcon color="primary" />
          <Typography variant="h6" component="span">
            Confirm Merge Request
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This will send a merge request to <strong>{targetUserName}</strong>.
            They must approve the request before any squares are transferred.
          </Typography>
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : boardPresence.length === 0 ? (
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>{offlinePlayer?.displayName}</strong> has no squares on any boards you manage.
            </Typography>
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Boards to merge ({boardPresence.length}):
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Board</TableCell>
                    <TableCell align="right">Squares</TableCell>
                    <TableCell align="right">Ledger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boardPresence.map((board) => (
                    <TableRow key={board.boardId}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {board.boardName}
                          {!board.targetUserIsMember && (
                            <PersonAddIcon
                              fontSize="small"
                              color="info"
                              titleAccess={`${targetUserName} will be added to this board`}
                              sx={{ fontSize: 16 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{board.squareCount}</TableCell>
                      <TableCell align="right">{board.ledgerEntries}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{totalSquares}</TableCell>
                    <TableCell align="right">{totalLedgerEntries}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {boardsNeedingAutoAdd > 0 && (
              <Alert severity="info" icon={<PersonAddIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{targetUserName}</strong> will be automatically added to{' '}
                  {boardsNeedingAutoAdd} board{boardsNeedingAutoAdd !== 1 ? 's' : ''} when they approve.
                </Typography>
              </Alert>
            )}

            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.200',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                What will happen:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. <strong>{targetUserName}</strong> will receive a merge request
              </Typography>
              <Typography variant="body2" color="text.secondary">
                2. They can approve or deny the request
              </Typography>
              <Typography variant="body2" color="text.secondary">
                3. If approved, {totalSquares} square{totalSquares !== 1 ? 's' : ''} across{' '}
                {boardPresence.length} board{boardPresence.length !== 1 ? 's' : ''} will be
                transferred
              </Typography>
              <Typography variant="body2" color="text.secondary">
                4. <strong>{offlinePlayer?.displayName}</strong> will be completely removed
              </Typography>
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                <Typography variant="caption">
                  The request will expire in 7 days if not acted upon.
                </Typography>
              </Alert>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
        >
          {submitting ? 'Sending...' : 'Send Merge Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalMergeConfirmDialog;
